/**
 * RemovablePill molecule — a labelled pill with a trailing × remove affordance
 * (e.g. a watchlist membership pill).
 *
 * Atoms only. Colors come from the Badge atom's `TONE_CLASS["info-soft"]`
 * pairing — the v7 design binds `info/info50` bg + `info/info800` text (dark:
 * rgb(5,64,93) / rgb(162,221,250)); the earlier `status-info-surface`/`status-info`
 * approximation measured rgb(29,40,45) / rgb(92,177,238) on device (SE-5522 pixel audit).
 * The label and the × are separate press targets: tapping the label calls
 * `onPress` (or `onRemove` when no `onPress` is given); tapping × always calls
 * `onRemove`.
 * @module components/molecules/RemovablePill/RemovablePillView
 */
import { X } from "lucide-react-native";
import React, { memo } from "react";

import type { PillTone } from "@/components/atoms/Badge";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for RemovablePillView */
interface RemovablePillViewProps {
  /** Pill label */
  readonly label: string;
  /**
   * Tone pairing to render.
   * @remarks
   * Defaults to the SE-5522 pixel-audited card pairing, so every existing
   * consumer is unchanged. The pipeline Table passes `info-subtle` because its
   * design draws the lighter `info/info0` fill (SE-6554).
   */
  readonly tone?: PillTone;
  /** Called when the × is tapped */
  readonly onRemove: () => void;
  /** Called when the label is tapped; falls back to onRemove when omitted */
  readonly onPress?: () => void;
  /** A11y label for the × — override when the action manages rather than removes */
  readonly removeAccessibilityLabel?: string;
  /** Optional testID forwarded to the remove (×) button */
  readonly testID?: string;
}

/**
 * Renders a removable pill.
 * @param root0 - Component props
 * @param root0.label - Pill label
 * @param root0.tone - Tone pairing (defaults to the card's `info-soft`)
 * @param root0.onRemove - Remove (×) handler
 * @param root0.onPress - Label-tap handler (defaults to onRemove)
 * @param root0.removeAccessibilityLabel - A11y label override for the ×
 * @param root0.testID - Optional testID for the × button
 */
const RemovablePillView = ({
  label,
  tone = "info-soft",
  onRemove,
  onPress,
  removeAccessibilityLabel,
  testID,
}: RemovablePillViewProps) => (
  <Stack
    direction="horizontal"
    className={`max-w-40 items-center gap-1 rounded-full px-2 py-1 ${TONE_CLASS[tone].box}`}
  >
    <Pressable
      onPress={onPress ?? onRemove}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Text
        variant="caption"
        className={TONE_CLASS[tone].text}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
    <Pressable
      testID={testID}
      onPress={onRemove}
      accessibilityRole="button"
      accessibilityLabel={removeAccessibilityLabel ?? `Remove ${label}`}
      hitSlop={{ top: 6, bottom: 6, left: 4, right: 6 }}
    >
      <Icon as={X} size="2xs" className={TONE_CLASS[tone].text} />
    </Pressable>
  </Stack>
);

RemovablePillView.displayName = "RemovablePillView";

export default memo(RemovablePillView);
